import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from './service/login-service/auth.service';
import { ConfigService } from '../../../services/config.service';
import { AppConfig } from '../../../models/config.model';
import { ImageTagComponent, ButtonComponent, InputComponent } from '../../ui';
import { LOGIN_TYPE } from '../../enums/common-enums/common-enums';
import { ErrorService } from '../../../shared/services/error.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule, ImageTagComponent, ButtonComponent, InputComponent, TranslateModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  config = signal<AppConfig | null>(null);
  isLoading = signal(false);
  
  // Common ISD codes
  isdCodes = [
    { code: '+1', country: 'US/CA' },
    { code: '+91', country: 'IN' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'AU' },
    { code: '+81', country: 'JP' },
    { code: '+86', country: 'CN' },
    { code: '+49', country: 'DE' },
    { code: '+33', country: 'FR' },
    { code: '+971', country: 'AE' },
    { code: '+966', country: 'SA' }
  ];
  
  selectedIsdCode = signal('+91');
  usernameType = signal<'email' | 'mobile'>(LOGIN_TYPE.MOBILE);

  protected errorService = inject(ErrorService);
  private translateService = inject(TranslateService);

  constructor(
    protected fb: FormBuilder,
    protected authService: AuthService,
    protected configService: ConfigService,
    protected router: Router
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadConfig();
  }

  private initializeForm(): void {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      isdCode: [this.selectedIsdCode()]
    });
  }

  private loadConfig(): void {
    this.configService.loadConfig().subscribe({
      next: (config) => {
        this.config.set(config);
      },
      error: (error) => {
        console.error('Failed to load config:', error);
        // Use default config
        this.config.set({
          logo: '/HCMS_LOGO.png',
          backgroundImage: '/HCMS_BACKGROUND_IMG.png',
          theme: {
            color1: '#2563eb',
            color2: '#1e40af',
            color3: '#3b82f6',
            color4: '#10b981',
            color5: '#f59e0b',
            backgroundColor: '#ffffff',
            textColor: '#1f2937',
            buttonColor: '#2563eb',
            buttonTextColor: '#ffffff',
            primaryColor: '#2563eb',
            secondaryColor: '#1e40af'
          },
          companyName: 'HCMS',
          poweredBy: 'HCMS'
        });
      }
    });
  }

  switchUsernameType(type: 'email' | 'mobile'): void {
    this.usernameType.set(type);
    this.loginForm.get('username')?.reset();
  }

  onIsdCodeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const isdCode = target.value;
    this.selectedIsdCode.set(isdCode);
    this.loginForm.patchValue({ isdCode });
  }

  isEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading.set(true);
      const { username, password } = this.loginForm.value;

      const isEmailFormat = this.isEmail(username);
      let loginIdentifier: string;

      if (isEmailFormat) {
        loginIdentifier = username.trim().toLowerCase();
      } else {
        loginIdentifier = username.replace(/[\s\-+]/g, '').trim();
      }

      this.authService.login(loginIdentifier, password, isEmailFormat ? 'email' : 'mobile')
        .subscribe({
          next: () => {
            this.isLoading.set(false);
            this.router.navigate(['/dashboard']);
          },
          error: (err: Error) => {
            this.isLoading.set(false);
            this.errorService.showError(err.message || this.translateService.instant('login.invalidCredentials'));
          }
        });
    } else {
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
    }
  }

  loginWithOTP(): void {
    // Navigate to OTP login page or handle OTP flow
    this.errorService.showInfo(this.translateService.instant('login.otpComingSoon'));
  }

  loginWithGoogle(): void {
    // Handle Google OAuth login
    this.errorService.showInfo(this.translateService.instant('login.googleComingSoon'));
  }

  forgotPassword(): void {
    // Navigate to forgot password page or handle forgot password flow
    this.errorService.showInfo(this.translateService.instant('login.forgotPasswordComingSoon'));
  }

  get backgroundImageUrl(): string {
    return this.config()?.backgroundImage || '/HCMS_BACKGROUND_IMG.png';
  }

  get logoUrl(): string {
    return this.config()?.logo || '/HCMS_LOGO_2.png';
  }

  get companyName(): string {
    return this.config()?.companyName || 'HCMS';
  }

  get poweredBy(): string {
    return this.config()?.poweredBy || 'HCMS';
  }
}
